import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faSpinner,
  faBullhorn,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import toast from "react-hot-toast";
import AnnouncementsService from "../../services/announcements.service";

function AnnouncementModal({
  isOpen,
  onClose,
  initialData,
  onSuccess,
  totalCount = 0,
}) {
  const isEditing = Boolean(initialData && initialData.id);

  const [formData, setFormData] = useState({
    text_ar: "",
    text_en: "",
    order: totalCount + 1,
    isActive: true,
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        text_ar: initialData.text_ar || "",
        text_en: initialData.text_en || "",
        order: initialData.order !== undefined ? initialData.order : 1,
        isActive:
          initialData.isActive !== undefined ? Boolean(initialData.isActive) : true,
      });
    } else {
      setFormData({
        text_ar: "",
        text_en: "",
        order: totalCount + 1,
        isActive: true,
      });
    }
  }, [initialData, isOpen, totalCount]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.text_ar.trim() && !formData.text_en.trim()) {
      toast.error("Please enter announcement text (Arabic or English)");
      return;
    }

    try {
      setLoading(true);
      if (isEditing) {
        await AnnouncementsService.update(initialData.id, formData);
        toast.success("Announcement updated successfully");
      } else {
        await AnnouncementsService.create(formData);
        toast.success("Announcement created successfully");
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving announcement:", error);
      const errMsg =
        error.response?.data?.message ||
        (Array.isArray(error.response?.data?.message)
          ? error.response.data.message.join(", ")
          : null) ||
        "Failed to save announcement";
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-[#f8fafc]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0388CC]/10 text-[#0388CC] flex items-center justify-center text-lg">
              <FontAwesomeIcon icon={faBullhorn} />
            </div>
            <div>
              <h3 className="text-[17px] font-bold text-[#033362]">
                {isEditing ? "Edit Announcement" : "New Announcement"}
              </h3>
              <p className="text-[12px] text-gray-500">
                {isEditing
                  ? "Update top bar announcement details"
                  : "Add a new banner announcement displayed at the top of the website"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-lg hover:bg-gray-100"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Arabic Text */}
          <div>
            <label className="block text-sm font-semibold text-[#033362] mb-1">
              Arabic Text <span className="text-red-500">*</span>
            </label>
            <textarea
              name="text_ar"
              value={formData.text_ar}
              onChange={handleChange}
              rows={3}
              dir="rtl"
              placeholder="Enter Arabic announcement text (e.g. سيدي هشام… غذاؤكم نرعاه بعناية)..."
              className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#0388CC]/30 focus:border-[#0388CC] text-sm text-right transition-all resize-none"
            />
          </div>

          {/* English Text */}
          <div>
            <label className="block text-sm font-semibold text-[#033362] mb-1">
              English Text
            </label>
            <textarea
              name="text_en"
              value={formData.text_en}
              onChange={handleChange}
              rows={3}
              dir="ltr"
              placeholder="e.g. Sidi Hisham — Nurturing your food with care"
              className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#0388CC]/30 focus:border-[#0388CC] text-sm transition-all resize-none"
            />
          </div>

          {/* Order and Active status */}
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div>
              <label className="block text-sm font-semibold text-[#033362] mb-1">
                Display Order
              </label>
              <input
                type="number"
                name="order"
                min="1"
                value={formData.order}
                onChange={handleChange}
                className="w-full px-3.5 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#0388CC]/30 focus:border-[#0388CC] text-sm"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Sequence order in the banner
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#033362] mb-1">
                Status
              </label>
              <label className="flex items-center gap-3 cursor-pointer mt-2">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#007530] relative"></div>
                <span
                  className={`text-sm font-medium ${
                    formData.isActive ? "text-[#007530]" : "text-gray-400"
                  }`}
                >
                  {formData.isActive ? "Active" : "Inactive"}
                </span>
              </label>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-600 font-medium text-sm hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#0388CC] hover:bg-[#026c9f] text-white font-medium text-sm transition-all shadow-md shadow-[#0388CC]/20 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheck} />
                  <span>{isEditing ? "Update" : "Save"}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AnnouncementModal;
